// map box gl css
import "mapbox-gl/dist/mapbox-gl.css";
import "@mantine/core/styles.css";
import "mantine-datatable/styles.layer.css";
import "react-chat-elements/dist/main.css";

import "./layout.css";

import { useEffect } from "react";

import { json, LoaderFunction } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteLoaderData,
  // Leaving these in because some code does use them, but it's
  // commented out and sometimes I uncomment them when needed.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useRouteError,
} from "@remix-run/react";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { captureRemixErrorBoundaryError } from "@sentry/remix";

import "leaflet/dist/leaflet.css";
import "@mantine/dates/styles.css";
import "@mantine/charts/styles.css";
import Shell from "./components/Shell";
import { getPresignedUrl } from "./db/helpers.server";
import { Hub, Notification, User } from "./db/models";
import { UserHub } from "./db/models";
import { db } from "./db/store.server";
import { useStore } from "./hooks/zustend";
import { getAllHubs, getHubById } from "./services/hub.server";
import {
  acknowledgeNotification,
  getNotificationsByUserId,
} from "./services/notification.server";
import { authenticator } from "./utils/auth.server";
import {
  getColorSchemeFromCookies,
  getColorSchemeScript,
} from "./utils/color-scheme";
import { throwIfResponseHubIdInvalid } from "./utils/loader.server";
import SimsiMantineProvider from "./utils/mantine";
import { createPerfLogger } from "./utils/perf-logger";
import { rbac } from "./utils/rbac";

export const loader: LoaderFunction = async ({ request, params }) => {
  const sessionUser = await authenticator.isAuthenticated(request);
  const url = new URL(request.url);
  const notifyId = url.searchParams.get("notifyId") || "";
  const requestStart = Date.now();
  const perf = createPerfLogger("root loader");
  const env = {
    ESRI_TILE_API_KEY: process.env.ESRI_TILE_API_KEY || "",
  };

  if (!sessionUser) {
    perf.logDuration("unauthenticated early-return", requestStart);
    const response = json({
      user: sessionUser,
      hubs: [],
      notifications: [],
      requestHeaders: Object.fromEntries(request.headers.entries()),
      initialHubId: undefined,
      initialHubName: undefined,
      isHubAdmin: false,
      hasRtmdxUsers: false,
      canEditCourses: false,
      canManageCrimeData: false,
      env,
    });
    perf.logDuration("total", requestStart);
    perf.logMilestones("unauthenticated");
    return response;
  }

  throwIfResponseHubIdInvalid(params.hubid);

  if (notifyId) {
    await acknowledgeNotification(notifyId, sessionUser.id);
  }

  let hubs: Hub[] = [];
  let notifications: Notification[] = [];
  let initialHubId: string | undefined =
    params?.hubid || sessionUser.lastHubId || undefined;
  let isHubAdmin = false;
  let hasRtmdxUsers = false;
  let canEditCourses = false;
  let canManageCrimeData = false;
  let initialHubName: string | undefined;

  if (sessionUser) {
    const userStart = Date.now();
    // Fetch user, hubs, notifications in parallel (minimal columns to avoid heavy eager loads)
    const [userWithProfile, fetchedHubs, fetchedNotifications] =
      await Promise.all([
        db.dataSource
          .getRepository(User)
          .createQueryBuilder("user")
          .leftJoinAndSelect("user.profile", "profile")
          .select([
            "user.id",
            "user.username",
            "user.email",
            "user.roles",
            "user.lastHubId",
            "profile.id",
            "profile.fileUrl",
            "profile.fileName",
          ])
          .where("user.id = :id", { id: sessionUser.id })
          .getOne(),
        getAllHubs(sessionUser.id, { selectMinimal: true }),
        getNotificationsByUserId(sessionUser.id),
      ]);
    perf.logDuration("fetch user/hubs/notifications", userStart);

    hubs = fetchedHubs;
    notifications = fetchedNotifications;

    // Update initial hub if not provided, using the freshly fetched hubs
    if (!initialHubId) {
      initialHubId = hubs?.[0]?.id;
    }

    const hasGlobalAdmin = Boolean(
      userWithProfile?.roles?.includes("admin") ||
        userWithProfile?.roles?.includes("superuser"),
    );
    const hasCoursesAdmin = Boolean(
      userWithProfile?.roles?.includes("courses_admin"),
    );

    // Fetch presigned URL for user's profile picture
    if (userWithProfile?.profile?.fileUrl) {
      const presignStart = Date.now();
      userWithProfile.profile.presignedUrl = await getPresignedUrl(
        userWithProfile.profile.fileUrl,
      );
      perf.logDuration("presign profile image", presignStart);
    }

    if (initialHubId && userWithProfile) {
      const rolesStart = Date.now();
      const rtmdxStart = Date.now();

      const userHubPromise = db.dataSource.getRepository(UserHub).findOne({
        where: { userId: userWithProfile.id, hubId: initialHubId },
        select: ["userId", "hubId", "roles"],
      });
      const rtmdxPromise = rbac.hasAnyUsersWithRoleInHub(
        initialHubId,
        "rtmdx",
      );

      const [userHub, rtmdxResult] = await Promise.all([
        userHubPromise,
        rtmdxPromise,
      ]);

      const hubRoles = userHub?.roles || [];

      const hasHubCoursesRole = hubRoles.includes("courses");
      const hasHubAdminRole = hubRoles.includes("admin");
      const hasDataManagerRole = hubRoles.includes("data_manager");

      isHubAdmin = hasGlobalAdmin || hasHubAdminRole;
      canEditCourses =
        hasGlobalAdmin ||
        hasCoursesAdmin ||
        hasHubCoursesRole ||
        hasHubAdminRole;
      canManageCrimeData =
        hasGlobalAdmin || hasHubAdminRole || hasDataManagerRole;
      perf.logDuration("role checks", rolesStart);

      hasRtmdxUsers = rtmdxResult;
      perf.logDuration("rtmdx check", rtmdxStart);

      // set initial hub name for non-member global admins to avoid ID flicker
      const hubMatch = hubs.find((hub) => hub.id === initialHubId);
      if (hubMatch) {
        initialHubName = hubMatch.name;
      } else if (hasGlobalAdmin) {
        const hubLookupStart = Date.now();
        const targetHub = await getHubById(initialHubId);
        initialHubName = targetHub?.name;
        perf.logDuration("lookup hub name for admin", hubLookupStart);
      }
    }

    const response = json({
      user: userWithProfile,
      hubs,
      notifications,
      requestHeaders: Object.fromEntries(request.headers.entries()),
      initialHubId,
      initialHubName,
      isHubAdmin,
      hasRtmdxUsers,
      canEditCourses,
      canManageCrimeData,
      env,
    });
    perf.logDuration("total", requestStart);
    perf.logMilestones("authenticated");
    return response;
  }

  // Fallback (should be unreachable because of early return)
  const fallbackResponse = json({
    user: sessionUser,
    hubs: [],
    notifications: [],
    requestHeaders: Object.fromEntries(request.headers.entries()),
    initialHubId: undefined,
    initialHubName: undefined,
    isHubAdmin: false,
    hasRtmdxUsers: false,
    canEditCourses: false,
    canManageCrimeData: false,
    env,
  });
  perf.logDuration("fallback", requestStart);
  perf.logDuration("total", requestStart);
  perf.logMilestones("fallback");
  return fallbackResponse;
};

export function Layout({ children }: { children: React.ReactNode }) {
  // Root-loader errors render this layout without loader data.
  const rootData = useRouteLoaderData<typeof loader>("root");
  const {
    user,
    notifications,
    requestHeaders,
    hubs,
    initialHubId,
    hasRtmdxUsers,
    env,
  } = rootData ?? {};
  const colorSchemeCookie = getColorSchemeFromCookies(
    requestHeaders?.cookie || requestHeaders?.Cookie,
  );

  const { currentHubId, setCurrentHubId, setLoggedUserId, loggedUserId } =
    useStore();

  const location = useLocation();
  const isIntakeRoute =
    location.pathname.includes("/cardiff/intake") ||
    location.pathname.includes("/invites/hubs/validate/") ||
    location.pathname.includes("/login/redirect/confirm");

  useEffect(() => {
    if (!currentHubId && initialHubId) setCurrentHubId(initialHubId);
    if (!loggedUserId) setLoggedUserId(user?.id);
  }, [
    currentHubId,
    initialHubId,
    loggedUserId,
    setCurrentHubId,
    setLoggedUserId,
    user?.id,
  ]);

  return (
    <html
      lang="en"
      data-mantine-color-scheme={colorSchemeCookie}
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />

        <Meta />
        <Links />
        <script
          data-mantine-script
          dangerouslySetInnerHTML={{
            __html: getColorSchemeScript(colorSchemeCookie),
          }}
        />
      </head>
      <body>
        <SimsiMantineProvider requestHeaders={requestHeaders}>
          {!rootData || isIntakeRoute ? (
            children
          ) : (
            <Shell
              notifications={notifications}
              requestHeaders={requestHeaders}
              currentHubId={currentHubId || initialHubId || hubs?.[0]?.id}
              hasRtmdxUsers={hasRtmdxUsers}
            >
              {children}
            </Shell>
          )}
        </SimsiMantineProvider>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env || {})}`,
          }}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// export const ErrorBoundary = () => {
//   const error = useRouteError();
//   captureRemixErrorBoundaryError(error);

//   if (
//     process.env.NODE_ENV === "development" ||
//     process.env.NODE_ENV === "test"
//   ) {
//     console.log({ error });
//   }

//   return <div>Something went wrong</div>;
// };

export default function App() {
  return <Outlet />;
}
